import React, { useRef, useState, useEffect } from 'react';
import YoutubeVideoElement from 'youtube-video-element/react';
import { PlayFilled } from '@weee-fe/kunlunicon';
import type { FeatureContentItem } from './index';
import PauseFilled from './pause-filled';
import { twMerge } from 'tailwind-merge';

interface MediaVideoProps {
  item: FeatureContentItem;
  className?: string;
  wrapperClassName?: string;
}

const MediaVideo: React.FC<MediaVideoProps> = ({ item, className }) => {
  const { mobile_media } = item;
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(true); // autoPlay 默认为播放状态
  const [isInView, setIsInView] = useState(false);

  const isYoutubeUrl = /^https?:\/\/(www\.)?(youtube\.com|youtu\.be)\//.test(mobile_media ?? '');

  // 懒加载：只有当视频进入视口时才加载
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect(); // 加载后不再监听
        }
      },
      {
        rootMargin: '200px', // 提前 200px 开始加载
        threshold: 0
      }
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  if (!mobile_media) return <></>;

  return (
    <div
      ref={containerRef}
      className="w-full h-full border border-solid border-tint-black-100 bg-[#ffffff] rounded-400 overflow-hidden relative flex items-center justify-center">
      {!isInView ? null : isYoutubeUrl ? (
        <YoutubeVideoElement
          className={twMerge('w-full h-full', className)}
          title="YouTube video player"
          src={mobile_media}
          controls
          autoplay
          muted
          preload="metadata"
          disablePictureInPicture
        />
      ) : (
        <>
          <video
            ref={videoRef}
            slot="media"
            src={mobile_media}
            autoPlay
            loop
            muted
            preload="metadata"
            disablePictureInPicture
            playsInline
            className="w-full h-full object-cover"
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            aria-label="Featured content video"
          />
          <div
            className="absolute top-3 right-3 w-5 h-5 bg-tint-black-650 rounded-full"
            style={{
              backdropFilter: 'blur(4px)'
            }}>
            {isPlaying ? (
              <span
                role="button"
                tabIndex={0}
                aria-label="Pause video"
                className="flex items-center justify-center w-full h-full"
                onClick={() => videoRef.current?.pause()}>
                <PauseFilled className="w-4 h-4 text-reserved-true-white" />
              </span>
            ) : (
              <span
                role="button"
                tabIndex={0}
                aria-label="Play video"
                className="flex items-center justify-center w-full h-full"
                onClick={() => videoRef.current?.play()}>
                <PlayFilled className="w-4 h-4 text-reserved-true-white" />
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default MediaVideo;
